<template>
  <teleport to="body">
    <WdsModal
      :show="open"
      :title="$t('Review supplier employment confirmation')"
      @cancel="close"
    >
      <WdsStack direction-column gap-md>
        <WdsText>
          {{
            $t(
              'The staffing agency has submitted their employment confirmation answers. Please review them below and approve if you are satisfied.',
            )
          }}
        </WdsText>

        <template v-if="loading">
          <WdsStack direction-column gap-sm>
            <WdsText>{{ $t('Loading...') }}</WdsText>
          </WdsStack>
        </template>

        <template v-else-if="supplierAnswers.length">
          <WdsStack
            v-for="(item, index) in supplierAnswers"
            :key="index"
            direction-column
            gap-sm
            class="tw-p-3 tw-border tw-border-gray-200 tw-rounded-lg"
          >
            <!-- Stored label is the canonical English question, shown translated here. -->
            <WdsText>{{ item.label ? $t(item.label) : '' }}</WdsText>
            <WdsText class="tw-font-semibold">{{ formatAnswer(item.value) }}</WdsText>
          </WdsStack>
        </template>
      </WdsStack>

      <template v-if="!hideFooter" #footer>
        <WdsStack justify-end align-center gap-sm class="tw-w-full">
          <WdsButton tertiary @click="close">
            {{ $t('Cancel') }}
          </WdsButton>

          <WdsButton
            success
            :disabled="isSubmitting || loading || !supplierAnswers.length"
            @click="handleApprove"
          >
            {{ isSubmitting ? $t('Approving...') : $t('Approve') }}
          </WdsButton>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { WdsButton, WdsModal, WdsStack, WdsText } from '@worksome/wds-components'
import queriesToRefetch from '../../queriesToRefetch'
import { graphql } from '@/gql'
import type { ComplianceDataField } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { ComplianceName } from '@/gql/graphql'
import { useToast } from '@/composables/useToast'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import type { ComplianceModalProps } from '@/components/Hire/HirePageCompliance/modals/complianceModals'

const ReviewQuery = graphql(`
  query NlSupplierReviewData($id: ID!, $names: [ComplianceName!]) {
    compliance(id: $id, names: $names) {
      name
      data {
        ... on ComplianceDataField {
          label
          value
        }
      }
    }
  }
`)

const toast = useToast()

const props = withDefaults(defineProps<ComplianceModalProps>(), {
  open: false,
  hideFooter: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const isSubmitting = ref(false)

const { mutate: updateCompliance } = useMutation(UpdateComplianceMutation)

const { result, loading } = useQuery(
  ReviewQuery,
  () => ({
    id: props.hireId,
    names: [ComplianceName.NlSupplierEmploymentConfirmationReview],
  }),
  () => ({
    enabled: props.open,
    fetchPolicy: 'network-only',
    clientId: 'publicApi',
  }),
)

const supplierAnswers = computed(() => {
  const data = result.value?.compliance?.[0]?.data ?? []
  return data.filter(
    (d): d is ComplianceDataField & { __typename: 'ComplianceDataField' } =>
      d.__typename === 'ComplianceDataField',
  )
})

// Localise the answer at display time, accepting any casing the data layer
// emits. ComplianceDataField.value is nullable / optional in codegen.
const formatAnswer = (value: string | null | undefined) => {
  const normalised = value?.toLowerCase()
  if (normalised === 'yes') return $t('Yes')
  if (normalised === 'no') return $t('No')
  return value ?? ''
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      isSubmitting.value = false
    }
  },
)

const close = () => {
  emit('update:open', false)
}

const handleApprove = async () => {
  isSubmitting.value = true

  try {
    await updateCompliance({
      input: {
        name: ComplianceName.NlSupplierEmploymentConfirmationReview,
        data: JSON.stringify({
          approved: true,
        }),
        hireId: props.hireId,
      },
    })

    await publicApi.refetchQueries({
      include: queriesToRefetch,
    })

    close()
    toast.success($t('Supplier employment confirmation approved'))
  } catch (error) {
    toast.error($t('An error occurred, please try again.'))
  } finally {
    isSubmitting.value = false
  }
}
</script>
